import { Component, StrictMode, type ErrorInfo, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import './styles/globals.css';

/**
 * Top-level error boundary. Kept inline and dependency-free so a render
 * failure deep in R3F still produces a useful black-screen recovery state
 * instead of a blank canvas.
 */
interface BoundaryState {
  readonly error: Error | null;
}

class RootErrorBoundary extends Component<{ children: ReactNode }, BoundaryState> {
  override state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Replace with structured logging once observability sink is wired.
     
    console.error('[tiw] root error', error, info.componentStack);
  }

  override render(): ReactNode {
    if (this.state.error) {
      return (
        <div
          role="alert"
          style={{
            position: 'fixed',
            inset: 0,
            display: 'grid',
            placeItems: 'center',
            background: '#04060a',
            color: '#ff6b6b',
            fontFamily: 'JetBrains Mono, ui-monospace, monospace',
            padding: '24px',
          }}
        >
          <div style={{ maxWidth: 640 }}>
            <div style={{ letterSpacing: '0.3em', fontSize: 11, opacity: 0.75 }}>
              UPLINK FAULT
            </div>
            <h1 style={{ margin: '8px 0 12px', fontSize: 22, color: '#d6f1ff' }}>
              Scene halted
            </h1>
            <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, opacity: 0.85 }}>
              {this.state.error.message}
            </pre>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const container = document.getElementById('root');
if (!container) {
  throw new Error('Root container #root is missing from index.html');
}

const root = createRoot(container);
root.render(
  <StrictMode>
    <RootErrorBoundary>
      <App />
    </RootErrorBoundary>
  </StrictMode>,
);

// Dismiss the boot splash after the first paint commits.
requestAnimationFrame(() => {
  const splash = document.getElementById('tiw-splash');
  if (!splash) return;
  splash.dataset.hidden = 'true';
  splash.addEventListener(
    'transitionend',
    () => splash.parentNode?.removeChild(splash),
    { once: true },
  );
});
